import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "./ui/button";

const CHUNK_INTERVAL_S = 25;

interface MicButtonProps {
  onAudioReady: (audioData: Float32Array) => void;
  disabled?: boolean;
}

async function blobToFloat32(blob: Blob): Promise<Float32Array> {
  const arrayBuffer = await blob.arrayBuffer();
  const audioCtx = new AudioContext({ sampleRate: 16000 });
  const decoded = await audioCtx.decodeAudioData(arrayBuffer);
  const float32 = decoded.getChannelData(0);
  audioCtx.close();
  return float32;
}

export function MicButton({ onAudioReady, disabled }: MicButtonProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [chunkCount, setChunkCount] = useState(0);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chunkTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const chunkIndexRef = useRef(0);
  const recordingRef = useRef(false);
  const onAudioReadyRef = useRef(onAudioReady);

  // Keep callback ref fresh to avoid stale closures
  useEffect(() => {
    onAudioReadyRef.current = onAudioReady;
  }, [onAudioReady]);

  // Record one chunk: creates a fresh MediaRecorder, records until stopped,
  // returns the complete WebM blob (with valid header).
  const recordChunk = useCallback(
    (stream: MediaStream): Promise<Blob> => {
      return new Promise((resolve) => {
        const chunks: Blob[] = [];
        const rec = new MediaRecorder(stream, {
          mimeType: "audio/webm;codecs=opus",
        });
        rec.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };
        rec.onstop = () => {
          resolve(new Blob(chunks, { type: "audio/webm" }));
        };
        rec.start(); // no timeslice — whole recording is one valid file
        // Store recorder so it can be stopped externally
        recorderRef.current = rec;
      });
    },
    []
  );

  const recorderRef = useRef<MediaRecorder | null>(null);

  // Flush one chunk: stop current recorder, decode, send to parent, restart
  const flushAndRestart = useCallback(async () => {
    if (!recordingRef.current || !streamRef.current) return;
    const rec = recorderRef.current;
    if (!rec || rec.state !== "recording") return;

    // Stop current recorder — this resolves the recordChunk promise
    rec.stop();
  }, []);

  const processBlob = useCallback(async (blob: Blob) => {
    chunkIndexRef.current += 1;
    setChunkCount(chunkIndexRef.current);
    try {
      const float32 = await blobToFloat32(blob);
      onAudioReadyRef.current(float32);
    } catch (err) {
      console.error("Chunk decode error:", err);
    }
  }, []);

  // The continuous recording loop
  const runContinuousLoop = useCallback(
    async (stream: MediaStream) => {
      while (recordingRef.current && stream.active) {
        const blob = await recordChunk(stream);
        if (blob.size > 0) {
          processBlob(blob);
        }
        // Small yield to allow state updates
        await new Promise((r) => setTimeout(r, 50));
      }
    },
    [recordChunk, processBlob]
  );

  const stopRecording = useCallback(() => {
    recordingRef.current = false;
    // Stop current recorder if active
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
    // Stop chunk flush timer
    if (chunkTimerRef.current) {
      clearInterval(chunkTimerRef.current);
      chunkTimerRef.current = null;
    }
    // Stop duration timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setIsRecording(false);
    setDuration(0);
    setChunkCount(0);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunkIndexRef.current = 0;
      setChunkCount(0);
      recordingRef.current = true;
      setIsRecording(true);
      startTimeRef.current = Date.now();

      // Duration timer
      timerRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);

      // Periodic chunk flush — stops and restarts the recorder every 25s
      chunkTimerRef.current = setInterval(() => {
        flushAndRestart();
      }, CHUNK_INTERVAL_S * 1000);

      // Start continuous recording loop
      runContinuousLoop(stream);
    } catch {
      console.error("Microphone access denied");
    }
  }, [flushAndRestart, runContinuousLoop]);

  useEffect(() => {
    return () => {
      recordingRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
      if (chunkTimerRef.current) clearInterval(chunkTimerRef.current);
    };
  }, []);

  const formatTime = (secs: number) => {
    const m = String(Math.floor(secs / 60)).padStart(2, "0");
    const s = String(secs % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <div className="flex items-center gap-3">
      {isRecording ? (
        <>
          <Button
            variant="destructive"
            size="lg"
            onClick={stopRecording}
            className="animate-pulse h-14 px-8 text-base sm:h-10 sm:px-6 sm:text-sm"
          >
            Stop Recording
          </Button>
          <span className="text-sm font-mono text-destructive">
            {formatTime(duration)}
          </span>
          {chunkCount > 0 && (
            <span className="text-xs text-muted-foreground">
              {chunkCount} chunk{chunkCount !== 1 ? "s" : ""} transcribed
            </span>
          )}
        </>
      ) : (
        <Button
          size="lg"
          onClick={startRecording}
          disabled={disabled}
          className="bg-red-600 hover:bg-red-700 text-white h-14 px-8 text-base sm:h-10 sm:px-6 sm:text-sm"
        >
          Record
        </Button>
      )}
    </div>
  );
}
